import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome to the atelier.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail) || "Could not sign in.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-melilux-bg text-melilux-cream font-sans grid grid-cols-12">
      {/* Left visual */}
      <div className="hidden md:flex col-span-7 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtaW5pbWFsaXN0JTIwbGl2aW5nJTIwcm9vbSUyMGZ1cm5pdHVyZXxlbnwwfHx8fDE3Nzk3MTk2ODR8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-melilux-bg/60" />
        <div className="relative z-10 p-14 flex flex-col justify-between w-full">
          <Link to="/" className="font-serif text-3xl" data-testid="login-brand">
            Melilux<span className="text-melilux-gold">.</span>
          </Link>
          <div>
            <p className="overline">Atelier console</p>
            <h1 className="font-serif text-5xl lg:text-6xl mt-5 leading-[0.95] max-w-md">
              A private door<br />
              <em className="serif-italic text-melilux-gold">for the makers.</em>
            </h1>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="col-span-12 md:col-span-5 flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <Link to="/" className="md:hidden font-serif text-2xl block mb-10">
            Melilux<span className="text-melilux-gold">.</span>
          </Link>
          <p className="overline">Sign in</p>
          <h2 className="font-serif text-4xl mt-3 leading-tight">
            Welcome back.
          </h2>
          <p className="text-sm text-melilux-cream/55 mt-4">
            Restricted access — administrators only.
          </p>

          <div className="mt-10 space-y-6">
            <input
              type="email"
              className="editorial-input"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              required
              autoComplete="email"
            />
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                className="editorial-input pr-10"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
                aria-label={showPassword ? "Hide password" : "Show password"}
                data-testid="login-password-toggle"
                className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-melilux-cream/80 hover:text-melilux-gold transition-colors"
              >
                {showPassword ? <EyeOff size={17} strokeWidth={1.7} /> : <Eye size={17} strokeWidth={1.7} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="mt-10 w-full bg-melilux-gold text-melilux-bg py-4 text-[0.72rem] uppercase tracking-[0.3em] hover:bg-melilux-goldBright transition-colors disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Enter Atelier"}
          </button>

          <Link
            to="/"
            className="block mt-8 text-center text-[0.7rem] uppercase tracking-[0.28em] text-melilux-cream/50 hover:text-melilux-gold transition-colors"
            data-testid="login-back"
          >
            ← Return to showcase
          </Link>
        </form>
      </div>
    </div>
  );
}
